import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material';
import {
  CardMembership as MembershipIcon,
  MoreHoriz as MoreHorizIcon,
  PauseCircleOutline as PauseIcon,
  CancelOutlined as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { getLocalizedValue } from '../../constants/Languages';
import CustomerPrimaryButton from '../customer/CustomerPrimaryButton';
import {
  CUSTOMER_COLORS,
  customerSectionPaperSx,
} from '../../constants/customerTheme';
import { brandAlpha } from '../../theme/brandTokens';

const MEMBERSHIP_ENTITLEMENT_TYPE = {
  UnlimitedUse: 'UnlimitedUse',
  FixedAllowancePerPeriod: 'FixedAllowancePerPeriod',
};

const ACTIVE_STATUSES = new Set(['Active', 'Trialing', 'PastDue', 'Paused']);

const manageMenuItemSx = {
  whiteSpace: 'normal',
  alignItems: 'center',
  minHeight: 48,
  py: 1,
  '& .MuiListItemIcon-root': {
    minWidth: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiListItemIcon-root .MuiSvgIcon-root': {
    fontSize: 22,
  },
};

const CustomerMembershipPlanCard = ({
  plan,
  currency,
  membershipStatus,
  isSubscribed,
  cancelAtPeriodEnd,
  cancelsAtUtc,
  isPaused,
  canCancel,
  canPause,
  canResume,
  canResumeCancellation,
  canSubscribe,
  canBook,
  showInStoreOnlyMessage,
  showBothModeHint,
  actionLoading,
  entitlementUsage,
  onSubscribe,
  onBook,
  onCancel,
  onPause,
  onResume,
  onResumeCancellation,
}) => {
  const { t, i18n } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const localizedName = getLocalizedValue(
    plan.name,
    i18n.language,
    undefined,
    t('customer.memberships.card.unnamed')
  );
  const isUnlimited = plan.entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse;
  const displayCurrency = plan.currency || currency;
  const isPendingCheckout = membershipStatus === 'PendingCheckout';
  const isActiveSubscription = membershipStatus && ACTIVE_STATUSES.has(membershipStatus);
  const canManage = Boolean(canPause || canCancel);
  const isMenuOpen = Boolean(menuAnchor);

  const statusLabel = membershipStatus
    ? t(`customer.memberships.status.${membershipStatus.charAt(0).toLowerCase()}${membershipStatus.slice(1)}`, {
        defaultValue: membershipStatus,
      })
    : null;

  const closeMenuAndRun = (action) => {
    setMenuAnchor(null);
    action?.();
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        ...customerSectionPaperSx,
        ...(isSubscribed && isActiveSubscription
          ? {
              borderColor: alpha('#2e7d32', 0.45),
              bgcolor: alpha('#2e7d32', 0.03),
            }
          : {}),
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
          borderColor: isSubscribed && isActiveSubscription
            ? alpha('#2e7d32', 0.55)
            : brandAlpha('accent', 0.35),
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <MembershipIcon sx={{ color: CUSTOMER_COLORS.primary, flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text }}>
              {localizedName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            {isSubscribed && statusLabel ? (
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  bgcolor: isActiveSubscription ? alpha('#2e7d32', 0.14) : brandAlpha('accent', 0.14),
                  color: isActiveSubscription ? '#2e7d32' : CUSTOMER_COLORS.primaryMuted,
                  fontWeight: 700,
                }}
              />
            ) : null}
            {canManage ? (
              <IconButton
                size="small"
                disabled={actionLoading}
                aria-label={t('customer.memberships.actions.manage')}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: brandAlpha('text', 0.06),
                  '&:hover': {
                    bgcolor: brandAlpha('text', 0.1),
                  },
                }}
              >
                <MoreHorizIcon fontSize="small" sx={{ color: CUSTOMER_COLORS.text }} />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={isMenuOpen}
          onClose={() => setMenuAnchor(null)}
          disableScrollLock
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 240,
              borderRadius: 2,
              boxShadow: `0 8px 24px ${brandAlpha('primary', 0.12)}`,
            },
          }}
        >
          {canPause ? (
            <MenuItem
              disabled={actionLoading}
              onClick={() => closeMenuAndRun(onPause)}
              sx={manageMenuItemSx}
            >
              <ListItemIcon>
                <PauseIcon sx={{ color: CUSTOMER_COLORS.text }} />
              </ListItemIcon>
              <ListItemText
                primary={t('customer.memberships.actions.pause')}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </MenuItem>
          ) : null}
          {canCancel ? (
            <MenuItem
              disabled={actionLoading}
              onClick={() => closeMenuAndRun(onCancel)}
              sx={{ ...manageMenuItemSx, color: '#c62828' }}
            >
              <ListItemIcon>
                <CancelIcon sx={{ color: '#c62828' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('customer.memberships.actions.cancel')}
                primaryTypographyProps={{ variant: 'body2', sx: { color: '#c62828' } }}
              />
            </MenuItem>
          ) : null}
        </Menu>

        <Typography variant="h5" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text, mb: 1 }}>
          {formatCurrency(plan.price, displayCurrency)}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {t('customer.memberships.card.perMonth')}
          </Typography>
        </Typography>

        <Chip
          label={
            isUnlimited
              ? t('customer.memberships.entitlement.unlimited')
              : t('customer.memberships.entitlement.allowance', { count: plan.allowanceQuantity })
          }
          size="small"
          sx={{
            alignSelf: 'flex-start',
            mb: entitlementUsage && isActiveSubscription ? 1 : 2,
            bgcolor: brandAlpha('accent', 0.14),
            color: CUSTOMER_COLORS.primaryMuted,
            fontWeight: 600,
          }}
        />

        {entitlementUsage && isActiveSubscription ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {entitlementUsage.entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse
              ? t('customer.memberships.usage.unlimited')
              : t('customer.memberships.usage.remaining', {
                  remaining: entitlementUsage.remainingQuantity ?? 0,
                  total: entitlementUsage.allowanceQuantity ?? 0,
                })}
          </Typography>
        ) : null}

        {plan.commitmentMonths ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('customer.memberships.card.commitment', { count: plan.commitmentMonths })}
          </Typography>
        ) : null}

        {cancelAtPeriodEnd && membershipStatus ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('customer.memberships.card.cancelsAtPeriodEnd', {
              date: formatUtcDate(cancelsAtUtc),
            })}
          </Typography>
        ) : null}

        {isPendingCheckout ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
            {t('customer.memberships.card.pendingCheckout')}
          </Typography>
        ) : null}

        {showInStoreOnlyMessage ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
            {t('customer.memberships.inStoreOnlyMessage')}
          </Typography>
        ) : null}

        {showBothModeHint ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('customer.memberships.bothModeHint')}
          </Typography>
        ) : null}

        <Box sx={{ mt: 'auto', pt: isPendingCheckout || showInStoreOnlyMessage ? 1 : 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {canResumeCancellation ? (
            <CustomerPrimaryButton
              fullWidth
              disabled={actionLoading}
              onClick={onResumeCancellation}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {actionLoading
                ? t('customer.memberships.actions.resumingCancellation')
                : t('customer.memberships.actions.resumeCancellation')}
            </CustomerPrimaryButton>
          ) : null}

          {canResume ? (
            <CustomerPrimaryButton
              fullWidth
              disabled={actionLoading}
              onClick={onResume}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {actionLoading ? t('customer.memberships.actions.resuming') : t('customer.memberships.actions.resume')}
            </CustomerPrimaryButton>
          ) : null}

          {/* Subscribing is what the card is for, so it leads; booking is the secondary way in. */}
          {canSubscribe ? (
            <CustomerPrimaryButton
              fullWidth
              disabled={actionLoading}
              onClick={() => onSubscribe(plan.id)}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {actionLoading
                ? t('customer.memberships.actions.subscribing')
                : t(isPendingCheckout ? 'customer.memberships.actions.continueCheckout' : 'customer.memberships.actions.subscribe')}
            </CustomerPrimaryButton>
          ) : null}

          {canBook && canSubscribe ? (
            <Button
              fullWidth
              variant="outlined"
              disabled={actionLoading}
              onClick={() => onBook?.(plan.id)}
              sx={{
                py: 1.1,
                fontWeight: 700,
                textTransform: 'none',
                color: CUSTOMER_COLORS.primaryMuted,
                borderColor: brandAlpha('accent', 0.6),
                '&:hover': { borderColor: CUSTOMER_COLORS.primary, bgcolor: brandAlpha('accent', 0.06) },
              }}
            >
              {t('customer.memberships.actions.bookAppointment')}
            </Button>
          ) : null}

          {canBook && !canSubscribe ? (
            <CustomerPrimaryButton
              fullWidth
              disabled={actionLoading}
              onClick={() => onBook?.(plan.id)}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {isActiveSubscription
                ? t('customer.memberships.actions.bookWithMembership')
                : t('customer.memberships.actions.bookAppointment')}
            </CustomerPrimaryButton>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerMembershipPlanCard;
